/**
 * `payments` endpoint wrappers — invoices, payments, ledger.
 *
 * Field shapes are derived from `serializeInvoice` / `serializeInvoiceRow` /
 * `serializeInvoiceAsPayment` / `serializePayment` / `serializePaymentRow` /
 * `serializeLedgerEntry` (refactor/backend/src/modules/payments/serializers.ts)
 * — see src/types/payment.ts for the full derivation notes. Route paths and
 * ordering are verified against refactor/backend/src/modules/payments/routes.ts.
 *
 * Two endpoints below return a BARE ARRAY, not `Paginated<T>`:
 * `getUpcomingPayments`/`getOverduePayments` (`upcoming/`, `overdue/`) and
 * `getOverdueInvoices` (`invoices/overdue/`). Do not wrap them in the
 * pagination-aware list pattern the other list pages use.
 */
import { apiClient } from '../client';
import { INVOICES, LEDGER, PAYMENTS, PAYMENTS_OVERDUE, PAYMENTS_UPCOMING } from './paths';
import type { Paginated } from '../types';
import type {
  Invoice,
  InvoiceAsPayment,
  InvoiceCreateRequest,
  InvoiceListRow,
  InvoicePatchRequest,
  LedgerEntry,
  Payment,
  PaymentListRow,
  RecordActualPaymentRequest,
} from '../../types/payment';

// ---------------------------------------------------------------------------
// payments/invoices/
// ---------------------------------------------------------------------------

/**
 * `GET payments/invoices/` — paginated, scoped to the caller; `status`/`search`/
 * `ordering` are server-side. `property` is a NON-PARITY addition (Django had no
 * such filter) — see `propertyFilterId` in the backend route file.
 */
export async function listInvoices(params?: {
  page?: number | undefined;
  status?: string | undefined;
  search?: string | undefined;
  ordering?: string | undefined;
  property?: number | undefined;
}): Promise<Paginated<InvoiceListRow>> {
  const response = await apiClient.get<Paginated<InvoiceListRow>>(INVOICES, { params });
  return response.data;
}

/** `GET payments/invoices/{id}/`. */
export async function getInvoice(id: number): Promise<Invoice> {
  const response = await apiClient.get<Invoice>(`${INVOICES}${id}/`);
  return response.data;
}

/**
 * `POST payments/invoices/`. Writes ONLY the invoice row — no ledger DEBIT.
 * Manager roles only (LANDLORD/AGENT/SUPERAGENT).
 */
export async function createInvoice(payload: InvoiceCreateRequest): Promise<Invoice> {
  const response = await apiClient.post<Invoice>(INVOICES, payload);
  return response.data;
}

/** `PATCH payments/invoices/{id}/` — partial update. */
export async function updateInvoice(id: number, payload: InvoicePatchRequest): Promise<Invoice> {
  const response = await apiClient.patch<Invoice>(`${INVOICES}${id}/`, payload);
  return response.data;
}

/** `DELETE payments/invoices/{id}/` — 409 if the invoice has payments/ledger entries. */
export async function deleteInvoice(id: number): Promise<void> {
  await apiClient.delete(`${INVOICES}${id}/`);
}

/** `GET payments/invoices/overdue/` — a BARE ARRAY of `InvoiceListRow`, not paginated. */
export async function getOverdueInvoices(): Promise<InvoiceListRow[]> {
  const response = await apiClient.get<InvoiceListRow[]>(`${INVOICES}overdue/`);
  return response.data;
}

/**
 * `POST payments/invoices/generate_monthly_invoices/` — LANDLORD/AGENT only
 * (a SUPERAGENT is 403 here, see routes.ts's permissions note).
 */
export async function generateMonthlyInvoices(): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(`${INVOICES}generate_monthly_invoices/`);
  return response.data;
}

// ---------------------------------------------------------------------------
// payments/payments/
// ---------------------------------------------------------------------------

/**
 * `GET payments/payments/` — paginated, scoped to the caller. `property` is a
 * NON-PARITY addition and filters through Payment -> Invoice -> Unit -> Property,
 * so a payment with no invoice never matches it.
 */
export async function listPayments(params?: {
  page?: number | undefined;
  ordering?: string | undefined;
  property?: number | undefined;
}): Promise<Paginated<PaymentListRow>> {
  const response = await apiClient.get<Paginated<PaymentListRow>>(PAYMENTS, { params });
  return response.data;
}

/** `GET payments/payments/{id}/`. */
export async function getPayment(id: number): Promise<Payment> {
  const response = await apiClient.get<Payment>(`${PAYMENTS}${id}/`);
  return response.data;
}

/**
 * `POST payments/payments/{invoiceId}/record_actual_payment/` — the ONLY
 * write in this module that actually settles a balance (via
 * `AccountService.recordPayment`): a `Payment` row plus a ledger CREDIT.
 * `invoiceId` is an INVOICE id, not a payment id — a DRF quirk of hanging
 * this action off `PaymentViewSet`. LANDLORD/AGENT only; a SUPERAGENT is 403.
 * Errors come back as `{error: string}`, not DRF field errors.
 */
export async function recordActualPayment(
  invoiceId: number,
  payload: RecordActualPaymentRequest,
): Promise<Payment> {
  const response = await apiClient.post<Payment>(
    `${PAYMENTS}${invoiceId}/record_actual_payment/`,
    payload,
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// payments/upcoming/, payments/overdue/ — bare-array feeds
// ---------------------------------------------------------------------------

/** `GET payments/upcoming/` — a BARE ARRAY of `InvoiceAsPayment`, not paginated. */
export async function getUpcomingPayments(): Promise<InvoiceAsPayment[]> {
  const response = await apiClient.get<InvoiceAsPayment[]>(PAYMENTS_UPCOMING);
  return response.data;
}

/** `GET payments/overdue/` — a BARE ARRAY of `InvoiceAsPayment`, not paginated. */
export async function getOverduePayments(): Promise<InvoiceAsPayment[]> {
  const response = await apiClient.get<InvoiceAsPayment[]>(PAYMENTS_OVERDUE);
  return response.data;
}

// ---------------------------------------------------------------------------
// payments/ledger/ — read-only
// ---------------------------------------------------------------------------

/** `GET payments/ledger/` — paginated, scoped to the caller. */
export async function listLedgerEntries(params?: {
  page?: number | undefined;
  ordering?: string | undefined;
}): Promise<Paginated<LedgerEntry>> {
  const response = await apiClient.get<Paginated<LedgerEntry>>(LEDGER, { params });
  return response.data;
}

/** `GET payments/ledger/{id}/`. */
export async function getLedgerEntry(id: number): Promise<LedgerEntry> {
  const response = await apiClient.get<LedgerEntry>(`${LEDGER}${id}/`);
  return response.data;
}
